import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ChildAttention } from '../../hooks/useChildAttention';

export interface PickerChild {
  id: string;
  displayName: string;
  locationName: string;
  status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
  badges?: { registrationPending: boolean };
}
interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  options: PickerChild[];
  selectedId?: string;
  /** Per-child things waiting: the total as an amber badge, the kinds as a line under the name. */
  attention?: Record<string, ChildAttention>;
  /** Shown under the name only when the family spans more than one location. */
  showLocation?: boolean;
  onPick: (child: PickerChild) => void;
  /** `${testIdPrefix}-${child.id}` on each row. */
  testIdPrefix: string;
}

/** A bottom sheet on the phone, a small panel under the top bar on `lg`. */
const ChildPickerSheet: React.FC<Props> = ({ open, onClose, title, options, selectedId, attention, showLocation, onPick, testIdPrefix }) => (
  <Dialog open={open} onClose={onClose} className="relative z-40">
    <div className="fixed inset-0 bg-gray-900/40" aria-hidden="true" />
    <div className="fixed inset-0 flex items-end justify-center lg:items-start lg:justify-end lg:pt-14 lg:pr-4">
      <DialogPanel data-testid="child-picker-sheet" className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-xl lg:w-80 lg:rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <DialogTitle className="text-sm font-semibold text-gray-900">{title}</DialogTitle>
          <button type="button" onClick={onClose} data-testid="child-picker-sheet-close" aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <ul className="divide-y divide-gray-100">
          {options.map((child) => {
            const count = attention?.[child.id]?.total ?? 0;
            return (
              <li key={child.id}>
                <button type="button" onClick={() => onPick(child)} data-testid={`${testIdPrefix}-${child.id}`} className="w-full flex items-center gap-3 py-3 text-left">
                  <Avatar name={child.displayName} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{child.displayName}</span>
                      {child.status === 'LEFT' && <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">Left</span>}
                      {child.badges?.registrationPending && <span data-testid={`${testIdPrefix}-registration-${child.id}`} className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Pending</span>}
                    </div>
                    {showLocation && <div className="text-xs text-gray-500 truncate">{child.locationName}</div>}
                    {count > 0 && <div data-testid={`${testIdPrefix}-breakdown-${child.id}`} className="text-xs text-amber-700 truncate">{attentionBreakdown(attention![child.id])}</div>}
                  </div>
                  {count > 0 && <span data-testid={`${testIdPrefix}-attention-${child.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1">{count}</span>}
                  {selectedId === child.id && <CheckIcon className="h-4 w-4 text-primary-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      </DialogPanel>
    </div>
  </Dialog>
);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "1 form · 2 policies · 3 observations" — only the kinds with something
 * waiting. Shared by the sheet and the chooser.
 */
export function attentionBreakdown(a: ChildAttention): string {
  return [
    a.registration > 0 ? 'registration pending' : null,
    a.forms > 0 ? plural(a.forms, 'form', 'forms') : null,
    a.policies > 0 ? plural(a.policies, 'policy', 'policies') : null,
    a.observations > 0 ? plural(a.observations, 'observation', 'observations') : null,
    a.messages > 0 ? plural(a.messages, 'note', 'notes') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Initials avatar — shared by the sheet, the switcher and the chooser. */
export const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'md' }) => {
  const initials = name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const cls = size === 'sm' ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm';
  return <div aria-hidden="true" className={`rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold flex-shrink-0 ${cls}`}>{initials || '?'}</div>;
};
export default ChildPickerSheet;
